import { Flex } from 'components/Flex';
import { BKJButton } from '@bookjane2/bookjane-design-library';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { QuerySwitch } from 'components/QuerySwitch';
import {
  RemovePositionDetailsHeaderWrapper,
  H3,
  PositionName,
  Text,
  TextLabel,
  JanesTableSubheader,
  JanesTableWrapper,
  RemovePositionDetailsLocationWrapper,
  JanesTableData,
  JanesTableRowInfoWrapper,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.styled';
import { useTranslation } from 'hooks/useTranslation';
import {
  useRemovePositionDataContext,
  RemovePositionDataProvider,
  RemovePositionDataContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/RemovePosition.data.context';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import {
  removePositionModalRoutingService,
  useRemovePositionModalUIContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { formatPlural } from 'utils/formatPlural';
import { BKJStickyTableHead, BKJTableBody } from 'components/BKJTable/BKJTable.styled';
import {
  BKJEllipsisName,
  BKJTableRowWrapper,
} from 'components/BKJTable/BKJTableRow/BKJTableRow.styled';
import { TeamMemberAvatar } from 'pages/TeamMemberPage/components/TeamMemberAvatar/TeamMemberAvatar';
import { RemovePositionModalJane } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.form.types';
import { Jane } from 'types/graphql';
import { RemovePositionDialog } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionDialog';
import { toastService } from 'services';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import { useFetchAssignedGroupedPositionsContext } from 'pages/LocationSettingsPage/PositionsAllocation/FetchAssignedGroupedPositions.data.context';

const mergeTeamMemberNodes = (existingNodes: Jane[], incomingNodes: Jane[]) => {
  const nextNodes = [...existingNodes];
  if (incomingNodes?.length > 0)
    incomingNodes.forEach((record) => {
      if (record && record.displayId)
        if (!nextNodes.some(({ displayId }) => record.displayId === displayId))
          nextNodes.push(record);
    });
  return nextNodes;
};

export const TeamMembersRow: FC<{
  displayId: string;
  firstName: string;
  lastName: string;
  isGrey: boolean;
  jane: RemovePositionModalJane;
}> = ({ displayId, firstName, lastName, isGrey, jane }) => {
  return (
    <JanesTableRowInfoWrapper isGrey={isGrey} key={`${displayId}-${firstName}`}>
      <BKJTableRowWrapper
        onClick={() => {
          removePositionModalRoutingService.open({ janeId: displayId }, 'JaneDetailsView');
        }}
      >
        <JanesTableData>
          <TeamMemberAvatar {...jane} />
          <BKJEllipsisName>{`${firstName} ${lastName}`}</BKJEllipsisName>
        </JanesTableData>
      </BKJTableRowWrapper>
    </JanesTableRowInfoWrapper>
  );
};

const TeamMemberComponent: FC = () => {
  const [isRemovePositionConfirmationOpen, setIsRemovePositionConfirmationOpen] = useState(false);

  const { data, loadMore } = useRemovePositionDataContext();
  const { locationId, positionId } = useRemovePositionModalUIContext();
  const { refetch: fetchAssignedPositions } = usePositionsAllocationSettingsPageDataContext();
  const { refetch } = useFetchAssignedGroupedPositionsContext();

  const handleClickRemovePosition = useCallback(
    (positionId: number, isShowRemoveOpenPositionsFlow: boolean) => {
      if (isShowRemoveOpenPositionsFlow) {
        return removePositionModalRoutingService.open({ positionId }, 'OpenShiftsView');
      }

      return setIsRemovePositionConfirmationOpen(true);
    },
    [],
  );
  const pageInfo = useMemo(() => {
    const info = data?.fetchPosition?.teamMembers?.pageInfo;
    return info || {};
  }, [data?.fetchPosition?.teamMembers?.pageInfo]);

  const { t } = useTranslation();

  const [teamMemberListData, setTeamMemberListData] = useState<Jane[]>([]);

  useEffect(() => {
    if (
      data &&
      data?.fetchPosition?.teamMembers?.nodes &&
      data?.fetchPosition?.teamMembers?.nodes.length
    ) {
      const lastElementFromApi = data?.fetchPosition?.teamMembers?.nodes.slice(-1)[0];
      if (
        teamMemberListData.length === 0 ||
        lastElementFromApi?.displayId !== teamMemberListData?.slice(-1)[0]?.displayId
      ) {
        setTeamMemberListData(
          mergeTeamMemberNodes(
            teamMemberListData,
            data?.fetchPosition?.teamMembers?.nodes as Jane[],
          ),
        );
      }
    }
  }, [data, teamMemberListData]);

  const location = data?.fetchPosition?.serviceableCommunities.find(
    (community) => community.displayId === locationId,
  );
  const isTeamMemberEmptyState = data?.fetchPosition?.teamMembers?.totalCount === 0;
  const isShowRemoveOpenPositionsFlow = data?.fetchPosition?.configuredShifts?.totalCount !== 0;
  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      height="100%"
      flexGrow={1}
    >
      {isRemovePositionConfirmationOpen && (
        <RemovePositionDialog
          positionId={Number(positionId)}
          communityId={locationId}
          communityName={location?.name}
          isRemovePositionConfirmationOpen={isRemovePositionConfirmationOpen}
          setIsRemovePositionConfirmationOpen={setIsRemovePositionConfirmationOpen}
          onSuccess={() => {
            toastService.success(t('success:SUCCESSFULLY_REMOVED_POSITION'));
            refetch && refetch();
            fetchAssignedPositions && fetchAssignedPositions();
            removePositionModalRoutingService.close();
          }}
        />
      )}
      <RemovePositionDetailsHeaderWrapper>
        <H3>{t('location_settings:REMOVE_POSITION_FROM_TEAM_MEMBERS')}</H3>
        <Flex justifyContent="space-between" alignItems="center">
          <PositionName>
            {t('common:POSITION')}: {data?.fetchPosition?.name}
          </PositionName>
          <Text noWrap>
            {`${data?.fetchPosition?.teamMembers?.totalCount} ${formatPlural(
              t('create_shifts:MEMBER').toLowerCase(),
              data?.fetchPosition?.teamMembers?.totalCount,
            )}`}
          </Text>
        </Flex>
        <RemovePositionDetailsLocationWrapper>
          <TextLabel>{t('common:LOCATION')}</TextLabel>
          <Text>{location?.name}</Text>
          {!isTeamMemberEmptyState && (
            <TextLabel>{t('location_settings:REMOVE_POSITION_MODAL_TM_REMOVE_MESSAGE')}</TextLabel>
          )}
        </RemovePositionDetailsLocationWrapper>
      </RemovePositionDetailsHeaderWrapper>
      {isTeamMemberEmptyState ? (
        <Flex height="100%" padding="40px 32px">
          <Text>{t('location_settings:TEAM_MEMBER_LIST_EMPTY_STATE_MESSAGE')}</Text>
        </Flex>
      ) : (
        <JanesTableWrapper>
          <BKJTableBody gridTemplateColumns="100%">
            <BKJStickyTableHead>
              <JanesTableSubheader>{t('common:TEAM_MEMBER')}</JanesTableSubheader>
            </BKJStickyTableHead>
            {teamMemberListData.map((jane, i) => (
              <TeamMembersRow
                key={`${i}-${jane.firstName}`}
                isGrey={i % 2 === 0}
                jane={jane as unknown as RemovePositionModalJane}
                {...jane}
              />
            ))}
            {!!pageInfo.hasNextPage && loadMore ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                width="100%"
                padding="24px 0px 0px 0px"
              >
                <InfiniteScrollLoading onViewportEnter={loadMore!} />
              </Flex>
            ) : null}
          </BKJTableBody>
        </JanesTableWrapper>
      )}
      <BKJSideModalFooter justifyContent="flex-end" gap="8px">
        <BKJButton
          variant="PurpleOutlined"
          onClick={() => removePositionModalRoutingService.close()}
          type="button"
          width="180px"
        >
          {t('auth:CANCEL')}
        </BKJButton>
        <BKJButton
          variant="PurpleSolid"
          width="180px"
          type="button"
          disabled={!isTeamMemberEmptyState}
          onClick={() => {
            handleClickRemovePosition(Number(positionId), isShowRemoveOpenPositionsFlow);
            setIsRemovePositionConfirmationOpen(true);
          }}
        >
          {t('auth:NEXT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};

export const PositionTeamMembersView: FC = () => {
  return (
    <RemovePositionDataProvider>
      <QuerySwitch context={RemovePositionDataContext} component={TeamMemberComponent} />
    </RemovePositionDataProvider>
  );
};
